import { produce } from "immer";

import { generateContent } from "@app/services/zoltar";
import {
  fetchFireflyImage,
  uploadFireflyImage,
  DEFAULT_FIREFLY_MODEL,
} from "@app/services/adobeFirefly";

import { getElements } from "@app/services/ccLibraries";
import { queryClient } from "./query";
import { storyKey } from "@app/hooks/stories";

const BATCH_SIZE = 10;
async function* generateVisualsRecursive(
  ims,
  prompt_template,
  length,
  result = { steps: [] },
) {
  for (let page = 1; page <= Math.ceil(length / BATCH_SIZE); page++) {
    console.log(
      `Generating visuals for page ${page} of ${Math.ceil(length / BATCH_SIZE)}`,
    );

    const currentPrompt = [...prompt_template];

    if (length > BATCH_SIZE) {
      currentPrompt.splice(
        -1,
        0,
        {
          prompt_template: JSON.stringify(result),
          role: "system",
        },
        {
          prompt_template: `You have already generated ${result.steps.length} steps. Continue generating until you reach ${Math.min(length, result.steps.length + BATCH_SIZE)} steps.`,
          role: "user",
        },
      );
    }

    const response = await generateContent(
      {
        user: ims.profile.email,
        prompt_template: currentPrompt,
      },
      "Failed to fetch visuals prompt",
    );

    if (response?.chat?.choices?.[0]?.finishReason !== "stop") {
      throw new Error("Failed to fetch visuals prompt", response);
    }

    const content = JSON.parse(
      response.chat.choices[0].message.content || '{"steps": []}',
    );

    result.steps.push(...content.steps);

    console.log({ content, result });

    yield content;
  }

  return result;
}

/**
 * Constructs the request body for the Firefly image generation API.
 *
 * @param {Object} options - The options for the image generation request.
 * @param {string} options.model - The model to use for image generation.
 * @param {string} options.prompt - The prompt for the image generation.
 * @param {string} options.contentClass - The content class for the image.
 * @param {string} options.detailLevel - The detail level for the image.
 * @param {Object} options.size - The size of the image.
 * @param {number} options.size.width - The width of the image.
 * @param {number} options.size.height - The height of the image.
 * @param {Array<number>} options.seeds - The seeds for the image generation.
 * @param {string|null} options.uploadId - The upload ID for the image.
 * @param {Array<string>} options.presets - The presets for the image generation.
 * @param {number} options.styleStrength - The style strength for the image.
 * @param {number} options.visualIntensity - The visual intensity for the image.
 *
 * @returns {Object} - The request body for the Firefly API.
 */
const getFireflyImageBody = ({
  model = DEFAULT_FIREFLY_MODEL,
  prompt = "",
  contentClass = "photo",
  detailLevel = "full",
  size = { width: 2688, height: 1536 },
  seeds = [],
  imageReferenceUrl = null,
  uploadId = null,
  presets = [],
  styleStrength = 50,
  visualIntensity = 8,
  upsamplerType = "default",
} = options) => ({
  model,
  prompt,
  contentClass,
  detailLevel,
  size,
  ...(seeds.length > 0 ? { seeds } : {}),
  ...(!!imageReferenceUrl || !!uploadId || presets.length > 0
    ? {
        style: {
          imageReference: {
            source: {
              ...(imageReferenceUrl ? { url: imageReferenceUrl } : {}),
              ...(uploadId ? { uploadId } : {}),
            },
          },
          presets,
          strength: styleStrength,
        },
      }
    : {}),
  visualIntensity,
  upsamplerType,
});

export const createPromptVisualsSlice = (set, get) => ({
  isPromptVisualsPending: false,
  promptVisualsResponse: null,
  /**
   * Generates visuals for the given story and step.
   *
   * @param {string} storyId - The ID of the story.
   * @param {string} stepId - The ID of the step.
   *
   * @throws {Error} If the required data is missing or if the generation fails.
   * @returns {Promise<void>} - A promise that resolves when the visuals are generated.
   */
  generateVisuals: async (storyId, stepId) => {
    set(
      { isPromptVisualsPending: true },
      undefined,
      "promptVisuals/generateVisuals",
    );

    get().addToast(`Generating story details`, {
      variant: "info",
      timeout: -1,
    });

    try {
      const ims = get().ims;
      const story = get().getStory(storyId);

      if (!ims?.token || !ims?.profile?.email || !story.industry) {
        throw new Error("Missing required data to generate visuals");
      }

      const selectedStep = story.script?.find((s) => s.id === stepId);

      if (typeof stepId !== "undefined" && !selectedStep) {
        throw new Error(`No step found with id: ${stepId}`);
      }

      console.log({ story });

      const extendedScript = produce(story.script, (draft) => {
        draft.forEach((step, sdx) => {
          step?.personas?.forEach((persona, pdx) => {
            const { name, descriptionPrompt } =
              story.personas.find((p) => p.id === persona) || {};

            if (!name || !descriptionPrompt) {
              console.warn(`No persona details found for id: ${persona}`);
            } else {
              draft[sdx].personas[pdx] = { name, descriptionPrompt };
            }
          });
        });
      });

      console.log({ extendedScript });

      const introductionTemplate = `Here's a customer journey story script as a JSON formatted array.

The story is set in the ${story.industry} industry for the company called ${story.customer}.

Title:
${story.title}

Overview:
${story.storyOverview}

Business goals:
${story.businessGoals}${
        !!story.campaignFocus
          ? `

Campaign focus: ${story.campaignFocus}`
          : ""
      }`;

      const visualsPromptTemplate = `${selectedStep ? `For the step with an *id* ${selectedStep.id} and the *title* "${selectedStep.title}` : `For each item`} in the script write a prompt that will be used to generate a image visualizing this step – "visualPrompt".
Using context of a step, describe in details the location in which it takes place.
Suggest a generic background photo that would fit the context of the step. In this case, use a generic description of the location or setting which will represent the ${story.industry} industry. Don't include any people, logos, icons, or text messages in the prompt. Don't mention any companies.
${false && `If the step has "visual" field, use it's content in the prompt as well.`}
${false && `If the step has "visualMessaging" field, compose the prompt to display that messaging.`}
At the end of the prompt add "Professional photography style."

Next, write a prompt that will be used to generate a visual for the step including specified personas – "personasPrompt". If there are specific personas mentioned in the "personas" field, include them in the scenario and use their "descriptionPrompt" to describe their appearance. Make sure to include all the details from "descriptionPrompt" field. Don't use personas names, but rather use their gender.
At the end of the prompt add "Professional photography style."

Take into consideration context of all the data in the provided script to with a cohesive narrative. Adjust prompts to represent specified "industry" as well as to be aligned with the whole story.
If any prompt mentions a device, like a phone or a computer, describe it as "with the screen turned off" to avoid generating anything on the screen.

Next, write a list of "tags" that will help to categorize the image. Generate short, single-word or brief phrase tags. Focus on key objects, people, actions, settings, and colors. Return only concise tags suitable for labeling, not full sentences. Make sure to include tags that represent the context of the step and the personas involved.

Make sure to generate prompts for each item in the script.

The output should contain a JSON formatted array of steps, where each step is an object with the following fields:
steps:
  *idx* – The 0-based index of the step in the story script
  *title* – The title of the step
  *visualPrompt*
  *personasPrompt*
  *tags*`;

      const prompt_template = [
        {
          prompt_template: JSON.stringify(extendedScript),
          role: "system",
        },
        {
          prompt_template: introductionTemplate,
          role: "user",
        },
        {
          prompt_template:
            "You are a prompt engineering expert specializing in image prompting.",
          role: "system",
        },
        {
          prompt_template: visualsPromptTemplate,
          role: "user",
        },
        {
          prompt_template:
            "You are an assistant designed to output JSON. Please make sure to output JSON only.",
          role: "system",
        },
      ];

      // if (selectedStep) {
      //   prompt_template.splice(0, prompt_template.length - 1, {
      //     prompt_template: `Generate output only for the step number ${selectedStep.id} with title "${selectedStep.title}"`,
      //     role: "user",
      //   });
      // }

      // const promptVisualsResponse = await generateContent(
      //   ims,
      //   {
      //     user: ims.profile.email,
      //     prompt_template,
      //   },
      //   "Failed to fetch visuals prompt",
      // );

      // console.log("promptVisualsResponse:", promptVisualsResponse);

      const parseResponsePromises = [];

      set({
        toGenerate: stepId ? 1 : extendedScript.length,
        currentGeneration: 0,
      });

      for await (const visualsContext of generateVisualsRecursive(
        ims,
        prompt_template,
        typeof stepId !== "undefined" ? 1 : extendedScript.length,
      )) {
        console.log("Visuals context:", visualsContext);
        parseResponsePromises.push(
          get().parsePromptVisualsResponse(
            storyId,
            visualsContext?.steps ?? [],
          ),
        );
      }

      await Promise.all(parseResponsePromises);

      set(
        { isPromptVisualsPending: false },
        undefined,
        "promptVisuals/generateVisuals",
      );

      if (typeof stepId === "undefined") {
        get().addToast(`Generation completed`, {
          variant: "positive",
          timeout: 1000 * 60, // 1 minute
          actionLabel: "Create Presentation",
          onAction: () => {
            set({ isExpressDialogOpen: true });
          },
        });
      }
    } catch (error) {
      console.error("Error generating visuals:", error);
      set(
        { isPromptVisualsPending: false },
        undefined,
        "promptVisuals/generateVisuals",
      );

      get().addToast(`Failed to generate story images`, {
        variant: "negative",
        timeout: -1,
        actionLabel: "Retry",
        shouldCloseOnAction: true,
        onAction: () => {
          get().generateVisuals(storyId, stepId);
        },
      });
    }
  },
  /**
   * Fetches visuals for the given story and step using persona cc library..
   *
   * @param {string} storyId - The ID of the story.
   * @param {string} stepId - The ID of the step.
   *
   * @throws {Error} If the required data is missing or if the generation fails.
   * @returns {Promise<void>} - A promise that resolves when the visuals are generated.
   */
  fetchVisuals: async (storyId, stepId) => {
    set(
      { isPromptVisualsPending: true },
      undefined,
      "promptVisuals/fetchVisuals",
    );

    get().addToast(`Generating story details`, {
      variant: "info",
      timeout: -1,
    });

    try {
      const ims = get().ims;
      const story = get().getStory(storyId);

      if (!ims || !story.industry) {
        throw new Error("Missing required data to generate visuals");
      }

      const selectedStep = story.script?.find((s) => s.id === stepId);

      if (stepId && !selectedStep) {
        throw new Error(`No step found with id: ${stepId}`);
      }

      console.log({ story });

      const libraries = [];

      // Use Promise.all to properly handle async operations
      await Promise.all(
        story.personas
          .filter((persona) => persona.libraryId) // Only fetch for personas with libraryId
          .map(async (persona) => {
            try {
              const images = await getElements({
                libraryId: persona.libraryId,
                tags: [],
              });

              libraries.push({
                name: persona.name,
                libraryId: persona.libraryId,
                images,
              });
            } catch (error) {
              console.warn(
                `Failed to fetch elements for persona ${persona.name}:`,
                error,
              );
              // Continue with other personas even if one fails
            }
          }),
      );

      const extendedScript = produce(story.script, (draft) => {
        draft.forEach((step, sdx) => {
          step?.personas?.forEach((persona, pdx) => {
            const { name, descriptionPrompt, libraryId, avatarId } =
              story.personas.find((p) => p.id === persona) || {};

            if (!name || !descriptionPrompt) {
              console.warn(`No persona details found for id: ${persona}`);
            } else {
              draft[sdx].personas[pdx] = {
                name,
                descriptionPrompt,
                libraryId,
                avatarId,
              };
            }
          });
        });
      });

      console.log({ extendedScript });

      const introductionTemplate = `Here are 2 documents: a customer journey story script as a JSON formatted array and a personas library as a JSON formatted array.

The story is set in the ${story.industry} industry for the company called ${story.customer}.

Title:
${story.title}

Overview:
${story.storyOverview}

Business goals:
${story.businessGoals}${
        !!story.campaignFocus
          ? `

Campaign focus: ${story.campaignFocus}`
          : ""
      }`;

      const visualsPromptTemplate = `${selectedStep ? `For the step with an *id* ${selectedStep.id} and the *title* "${selectedStep.title}` : `For each item`} in the script write a prompt that will be used to generate a image visualizing this step – "visualPrompt".
Using context of a step, describe in details the location in which it takes place.
Suggest a generic background photo that would fit the context of the step. In this case, use a generic description of the location or setting which will represent the ${story.industry} industry. Don't include any people, logos, icons, or text messages in the prompt. Don't mention any companies.
${false && `If the step has "visual" field, use it's content in the prompt as well.`}
${false && `If the step has "visualMessaging" field, compose the prompt to display that messaging.`}
At the end of the prompt add "Professional photography style."

Next, write a prompt that will be used to generate a visual for the step including specified personas – "personasPrompt". If there are specific personas mentioned in the "personas" field, include them in the scenario and use their "descriptionPrompt" to describe their appearance. Make sure to include all the details from "descriptionPrompt" field. Don't use personas names, but rather use their gender.
At the end of the prompt add "Professional photography style."

Take into consideration context of all the data in the provided script to with a cohesive narrative. Adjust prompts to represent specified "industry" as well as to be aligned with the whole story.
If any prompt mentions a device, like a phone or a computer, describe it as "with the screen turned off" to avoid generating anything on the screen.

Make sure to generate prompts for each item in the script.`;

      const personasPromptTemplate = `${selectedStep ? `For the step with an *id* ${selectedStep.id} and the *title* "${selectedStep.title}` : `For each item`} in the script find the best matching image from the provided personas library.

1. For each persona mentioned in the step's "personas" field:
   - Find the matching persona in personas library by comparing the persona's "libraryId" field
   - Each persona item consists of "name", "libraryId", "images" fields.
   - Look through that persona's "images" array to find the best matching image.
   - Do not assume that the first image is the best match, but rather find the best match based on the scoring criteria.
   - Do not alter the original image's "id" or "name" fields. Take them exactly as they are in the library.

2. When selecting the best image, consider:
   - The step's context and title
   - The persona's description and role in the step
   - The generated visualPrompt and personasPrompt
   - The image's "description" and "tags" fields
   - How well the image fits the step's setting and action (the image shouldn't be a generic setting, but rather a specific setting that matches the step's context)

3. Scoring criteria for image selection:
   - Relevance to step context (highest priority)
   - Match with persona's role and description (if the persona is a customer, the image should be a customer setting)
   - Appropriate setting/environment (the image should be a specific setting that matches the step's context)
   - Professional appearance (the image should be a professional appearance)
   - Industry-appropriate attire and setting (the image should be an industry-appropriate attire and setting)

4. For the selected image, save:
   - "libraryId": The persona's library ID
   - "visualId": The selected image's "id" field
   - "visualName": The selected image's "name" field

If no personas are mentioned in the step, or if no suitable images are found, set both "libraryId" and "visualId" to null.

Next, check if the selected "visualId" is present in the "images" array of the persona with selected "libraryId". If it is not, start over and find the best matchin image.
`;

      const prompt_template = [
        {
          prompt_template: JSON.stringify(extendedScript),
          role: "system",
        },
        {
          prompt_template: JSON.stringify(libraries),
          role: "system",
        },
        {
          prompt_template: introductionTemplate,
          role: "user",
        },
        {
          prompt_template:
            "You are a prompt engineering expert specializing in image prompting.",
          role: "system",
        },
        {
          prompt_template: visualsPromptTemplate,
          role: "user",
        },
        {
          prompt_template: personasPromptTemplate,
          role: "user",
        },
        {
          prompt_template: `The output should contain a JSON formatted array of steps, where each step is an object with the following fields:
            steps:
              *idx* – The 0-based index of the step in the story script
              *title* – The title of the step
              *visualPrompt*
              *personasPrompt*
              *libraryId* – The library ID of the selected image (or null if none selected)
              *visualId* – The ID of the selected image (or null if none selected),
              *visualName* – The name of the selected image (or null if none selected)`,
          role: "user",
        },
        {
          prompt_template:
            "You are an assistant designed to output JSON. Please make sure to output JSON only.",
          role: "system",
        },
      ];

      // if (selectedStep) {
      //   prompt_template.splice(0, prompt_template.length - 1, {
      //     prompt_template: `Generate output only for the step number ${selectedStep.id} with title "${selectedStep.title}"`,
      //     role: "user",
      //   });
      // }

      // const promptVisualsResponse = await generateContent(
      //   ims,
      //   {
      //     user: ims.profile.email,
      //     prompt_template,
      //   },
      //   "Failed to fetch visuals prompt",
      // );

      // console.log("promptVisualsResponse:", promptVisualsResponse);

      const parseResponsePromises = [];

      set({
        toGenerate: stepId ? 1 : extendedScript.length,
        currentGeneration: 0,
      });

      for await (const visualsContext of generateVisualsRecursive(
        ims,
        prompt_template,
        stepId ? 1 : extendedScript.length,
      )) {
        console.log("Visuals context:", visualsContext);
        parseResponsePromises.push(
          get().parseFetchVisualsResponse(storyId, visualsContext?.steps ?? []),
        );
      }

      await Promise.all(parseResponsePromises);

      set(
        { isPromptVisualsPending: false },
        undefined,
        "promptVisuals/fetchVisuals",
      );

      get().addToast(`Generation completed`, {
        variant: "positive",
        timeout: 1000 * 60, // 1 minute
        actionLabel: "Create Presentation",
        onAction: () => {
          set({ isExpressDialogOpen: true });
        },
      });
    } catch (error) {
      console.error("Error generating visuals:", error);
      set(
        { isPromptVisualsPending: false },
        undefined,
        "promptVisuals/fetchVisuals",
      );

      if (!stepId) {
        get().addToast(`Failed to generate story images`, {
          variant: "negative",
          timeout: -1,
          actionLabel: "Retry",
          shouldCloseOnAction: true,
          onAction: () => {
            get().fetchVisuals(storyId);
          },
        });
      }
    }
  },
  /**
   *
   * @param {string} storyId
   * @param {*} visualsContext
   *
   * @returns {Promise<void>} - A promise that resolves when the visuals are parsed and updated in the story.
   * @throws {Error} If the response is invalid or if the parsing fails.
   */
  parsePromptVisualsResponse: async (storyId, visualsContext) => {
    try {
      // if (promptVisualsResponse?.chat?.choices?.[0]?.finishReason !== "stop") {
      //   // TODO: handle LLM error response
      //   throw new Error(
      //     "Failed to fetch visuals prompt",
      //     promptVisualsResponse,
      //   );
      // }

      // const visualsContext =
      //   JSON.parse(
      //     promptVisualsResponse?.chat?.choices?.[0]?.message?.content ||
      //       '{"steps": []}',
      //   )?.steps || [];

      console.log({ visualsContext });

      // set(
      //   produce((state) => {
      //     visualsContext.forEach((step, idx) => {
      //       const storyIndex = state.stories.findIndex(
      //         (story) => story.id === storyId,
      //       );
      //       if (storyIndex === -1) {
      //         throw new Error("Story not found");
      //       }

      //       state.stories[storyIndex].script[step.idx ?? idx].visualPrompt =
      //         state.stories[storyIndex].script[step.idx ?? idx]?.personas
      //           ?.length
      //           ? (step?.personasPrompt ?? step.visualPrompt)
      //           : step.visualPrompt;
      //       state.stories[storyIndex].script[step.idx ?? idx].visualTags =
      //         step.tags;
      //     });
      //   }),
      //   undefined,
      //   "promptVisuals/parsePromptVisualsResponse",
      // );

      // get().saveStory(storyId);
      queryClient.setQueryData(storyKey.detail(storyId), (oldData) =>
        produce(oldData, (state) => {
          visualsContext.forEach((step, idx) => {
            state.script[step.idx ?? idx].visualPrompt = state.script[
              step.idx ?? idx
            ]?.personas?.length
              ? (step?.personasPrompt ?? step.visualPrompt)
              : step.visualPrompt;
            state.script[step.idx ?? idx].visualTags = step.tags;
          });
        }),
      );

      get().addToast(
        `Generating story images: ${get().currentGeneration}/${get().toGenerate}`,
        {
          variant: "info",
          timeout: -1,
        },
      );

      await get().fetchImageReference(storyId);

      const visualsPromises = visualsContext.map((step) => {
        try {
          const story = get().getStory(storyId);
          if (!story) {
            console.warn(`No story found with id: ${storyId}`);
            return;
          }

          const storyStep = story.script[step.idx];
          if (!storyStep) {
            console.warn(
              `No story step found for index ${step.idx} in visuals context`,
            );
            return;
          }

          const visualPrompt = storyStep?.personas?.length
            ? (step?.personasPrompt ?? step.visualPrompt)
            : step.visualPrompt;

          if (!visualPrompt || typeof step.idx !== "number") {
            return;
          }

          console.log("visual prompt:", step.idx, visualPrompt);
          const personaAvatarSeed =
            story?.personas?.[storyStep?.personas?.[0]]?.avatarSeed;

          return fetchFireflyImage(
            storyId,
            getFireflyImageBody({
              prompt: visualPrompt,
              ...(personaAvatarSeed ? { seeds: [personaAvatarSeed] } : {}),
              uploadId: get().imageReferenceResponse?.id,
              // imageReferenceUrl: get().imageReference?.internalUrl,
            }),
          ).then((visualImages) => {
            if (!visualImages?.length) {
              console.warn(
                `No images found for step ${step.idx} with prompt: ${visualPrompt}`,
              );
              return;
            }

            set((state) => ({
              currentGeneration: state.currentGeneration + 1,
            }));

            get().addToast(
              `Generating story images: ${get().currentGeneration}/${get().toGenerate}`,
              {
                variant: "info",
                timeout: -1,
              },
            );

            //   generationToast(
            //     `Generating visuals: ${counter}/${visualsContext.length}`,
            //   );

            console.log("visual images:", step.idx, visualImages);

            // set(
            //   produce((state) => {
            //     const storyIndex = state.stories.findIndex(
            //       (story) => story.id === storyId,
            //     );
            //     if (storyIndex === -1) {
            //       throw new Error("Story not found");
            //     }

            //     state.stories[storyIndex].script[step.idx].visualUrl =
            //       visualImages?.[0]?.url;
            //     state.stories[storyIndex].script[step.idx].visualSeed =
            //       visualImages?.[0]?.seed;
            //     state.stories[storyIndex].script[step.idx].visuals =
            //       state.stories[storyIndex].script[step.idx]?.visuals ?? [];
            //     state.stories[storyIndex].script[step.idx].visuals.push({
            //       url: visualImages?.[0]?.url,
            //       seed: visualImages?.[0]?.seed,
            //       path: visualImages?.[0]?.path,
            //       prompt: visualPrompt,
            //     });

            //     state.stories[storyIndex].script[step.idx].visual =
            //       state.stories[storyIndex].script[step.idx].visuals.length - 1;
            //   }),
            //   undefined,
            //   "promptVisuals/parsePromptVisualsResponse",
            // );

            queryClient.setQueryData(storyKey.detail(storyId), (oldData) =>
              produce(oldData, (state) => {
                state.script[step.idx].visuals =
                  state.script[step.idx]?.visuals ?? [];
                state.script[step.idx].visuals.push({
                  url: visualImages?.[0]?.url,
                  seed: visualImages?.[0]?.seed,
                  path: visualImages?.[0]?.path,
                  prompt: visualPrompt,
                  type: "firefly",
                });

                state.script[step.idx].visual =
                  state.script[step.idx].visuals.length - 1;
              }),
            );
          });
        } catch (error) {
          console.error(`Failed to fetch image for step ${step.title}:`, error);
          return Promise.reject(error);
        }
      });

      // Promise.race(visualsContext).then(() => {
      //   set(
      //     { promptVisualsResponse: promptVisualsResponse },
      //     undefined,
      //     "promptVisuals/parsePromptVisualsResponse",
      //   );
      // });

      await Promise.allSettled(visualsPromises);

      // get().saveStory(storyId);

      //   generationToast("Generation completed", {
      //     variant: "positive",
      //     actionLabel: "Create Presentation",
      //     onAction: () => {
      //       setState((state) => ({
      //         ...state,
      //         isExpressDialogOpen: true,
      //       }));
      //     },
      //   });

      // await new Promise(() => {
      //   setTimeout(() => {
      //     // generationToast("", { isGenerating: false })

      //     resolve();
      //   }, 10000);
      // });
    } catch (error) {
      console.error("Error parsing visuals response:", error);
      get().addToast(`Failed to generate story images`, {
        variant: "negative",
        timeout: -1,
        actionLabel: "Retry",
        shouldCloseOnAction: true,
        onAction: () => {
          get().generateVisuals(storyId);
        },
      });
    }
  },
  /**
   *
   * @param {string} storyId
   * @param {*} visualsContext
   *
   * @returns {Promise<void>} - A promise that resolves when the visuals are parsed and updated in the story.
   * @throws {Error} If the response is invalid or if the parsing fails.
   */
  parseFetchVisualsResponse: async (storyId, visualsContext) => {
    try {
      // if (promptVisualsResponse?.chat?.choices?.[0]?.finishReason !== "stop") {
      //   // TODO: handle LLM error response
      //   throw new Error(
      //     "Failed to fetch visuals prompt",
      //     promptVisualsResponse,
      //   );
      // }

      const ims = get().ims;

      // const visualsContext =
      //   JSON.parse(
      //     promptVisualsResponse?.chat?.choices?.[0]?.message?.content ||
      //       '{"steps": []}',
      //   )?.steps || [];

      console.log({ visualsContext });

      // set(
      //   produce((state) => {
      //     visualsContext.forEach((step, idx) => {
      //       const storyIndex = state.stories.findIndex(
      //         (story) => story.id === storyId,
      //       );
      //       if (storyIndex === -1) {
      //         throw new Error("Story not found");
      //       }

      //       state.stories[storyIndex].script[step.idx ?? idx].visualPrompt =
      //         state.stories[storyIndex].script[step.idx ?? idx]?.personas
      //           ?.length
      //           ? (step?.personasPrompt ?? step.visualPrompt)
      //           : step.visualPrompt;
      //       state.stories[storyIndex].script[step.idx ?? idx].visualId = state
      //         .stories[storyIndex].script[step.idx ?? idx]?.personas?.length
      //         ? step.visualId
      //         : null;
      //     });
      //   }),
      //   undefined,
      //   "promptVisuals/parseFetchVisualsResponse",
      // );

      // get().saveStory(storyId);
      queryClient.setQueryData(storyKey.detail(storyId), (oldData) =>
        produce(oldData, (state) => {
          visualsContext.forEach((step, idx) => {
            state.script[step.idx ?? idx].visualPrompt = state.script[
              step.idx ?? idx
            ]?.personas?.length
              ? (step?.personasPrompt ?? step.visualPrompt)
              : step.visualPrompt;

            const visualPrompt = state.script[step.idx ?? idx]?.personas?.length
              ? (step?.personasPrompt ?? step.visualPrompt)
              : step.visualPrompt;

            state.script[step.idx ?? idx].visualPrompt = visualPrompt;

            if (step.visualId && step.libraryId) {
              state.script[step.idx].visuals =
                state.script[step.idx]?.visuals ?? [];

              state.script[step.idx].visuals.push({
                id: step.visualId,
                libraryId: step.libraryId,
                visualName: step.visualName,
                prompt: visualPrompt,
                type: "cc-library",
              });

              state.script[step.idx].visual =
                state.script[step.idx].visuals.length - 1;
            }
          });
        }),
      );

      get().addToast(
        `Generating story images: ${get().currentGeneration}/${get().toGenerate}`,
        {
          variant: "info",
          timeout: -1,
        },
      );

      await get().fetchImageReference(storyId);

      const visualsPromises = visualsContext.map((step) => {
        try {
          const story = get().getStory(storyId);
          if (!story) {
            console.warn(`No story found with id: ${storyId}`);
            return;
          }

          const storyStep = story.script[step.idx];
          if (!storyStep) {
            console.warn(
              `No story step found for index ${step.idx} in visuals context`,
            );
            return;
          }

          const visualPrompt = storyStep?.personas?.length
            ? (step?.personasPrompt ?? step.visualPrompt)
            : step.visualPrompt;

          if (!visualPrompt || typeof step.idx !== "number") {
            return;
          }

          console.log("visual prompt:", step.idx, visualPrompt);

          const personaAvatarSeed = null;

          if (!step.visualId) {
            return fetchFireflyImage(
              storyId,
              getFireflyImageBody({
                prompt: visualPrompt,
                ...(personaAvatarSeed ? { seeds: [personaAvatarSeed] } : {}),
                uploadId: get().imageReferenceResponse?.id,
                // imageReferenceUrl: get().imageReference?.internalUrl,
              }),
            ).then((visualImages) => {
              if (!visualImages?.length) {
                console.warn(
                  `No images found for step ${step.idx} with prompt: ${visualPrompt}`,
                );
                return;
              }

              set((state) => ({
                currentGeneration: state.currentGeneration + 1,
              }));

              get().addToast(
                `Generating story images: ${get().currentGeneration}/${get().toGenerate}`,
                {
                  variant: "info",
                  timeout: -1,
                },
              );

              //   generationToast(
              //     `Generating visuals: ${counter}/${visualsContext.length}`,
              //   );

              console.log("visual images:", step.idx, visualImages);

              // set(
              //   produce((state) => {
              //     const storyIndex = state.stories.findIndex(
              //       (story) => story.id === storyId,
              //     );
              //     if (storyIndex === -1) {
              //       throw new Error("Story not found");
              //     }

              //     state.stories[storyIndex].script[step.idx].visualUrl =
              //       visualImages?.[0]?.url;
              //     state.stories[storyIndex].script[step.idx].visualSeed =
              //       visualImages?.[0]?.seed;
              //     state.stories[storyIndex].script[step.idx].visuals =
              //       state.stories[storyIndex].script[step.idx]?.visuals ?? [];
              //     state.stories[storyIndex].script[step.idx].visuals.push({
              //       url: visualImages?.[0]?.url,
              //       seed: visualImages?.[0]?.seed,
              //       path: visualImages?.[0]?.path,
              //       prompt: visualPrompt,
              //     });

              //     state.stories[storyIndex].script[step.idx].visual =
              //       state.stories[storyIndex].script[step.idx].visuals.length -
              //       1;
              //   }),
              //   undefined,
              //   "promptVisuals/parsePromptVisualsResponse",
              // );

              queryClient.setQueryData(storyKey.detail(storyId), (oldData) =>
                produce(oldData, (state) => {
                  state.script[step.idx].visuals =
                    state.script[step.idx]?.visuals ?? [];
                  state.script[step.idx].visuals.push({
                    url: visualImages?.[0]?.url,
                    seed: visualImages?.[0]?.seed,
                    path: visualImages?.[0]?.path,
                    prompt: visualPrompt,
                    type: "firefly",
                  });

                  state.script[step.idx].visual =
                    state.script[step.idx].visuals.length - 1;
                }),
              );
            });
          } else {
            // set(
            //   produce((state) => {
            //     const storyIndex = state.stories.findIndex(
            //       (story) => story.id === storyId,
            //     );
            //     if (storyIndex === -1) {
            //       throw new Error("Story not found");
            //     }

            //     state.stories[storyIndex].script[step.idx].visuals =
            //       state.stories[storyIndex].script[step.idx]?.visuals ?? [];
            //     state.stories[storyIndex].script[step.idx].visuals.push({
            //       id: step.visualId,
            //       libraryId: step.libraryId,
            //       visualName: step.visualName,
            //       prompt: visualPrompt,
            //     });

            //     state.stories[storyIndex].script[step.idx].visual =
            //       state.stories[storyIndex].script[step.idx].visuals.length - 1;
            //   }),
            //   undefined,
            //   "promptVisuals/parsePromptVisualsResponse",
            // );

            queryClient.setQueryData(storyKey.detail(storyId), (oldData) =>
              produce(oldData, (state) => {
                state.script[step.idx].visuals =
                  state.script[step.idx]?.visuals ?? [];
                state.script[step.idx].visuals.push({
                  id: step.visualId,
                  libraryId: step.libraryId,
                  visualName: step.visualName,
                  prompt: visualPrompt,
                  type: "firefly",
                });

                state.script[step.idx].visual =
                  state.script[step.idx].visuals.length - 1;
              }),
            );
          }
        } catch (error) {
          console.error(`Failed to fetch image for step ${step.title}:`, error);
          return Promise.reject(error);
        }
      });

      // Promise.race(visualsContext).then(() => {
      //   set(
      //     { promptVisualsResponse: promptVisualsResponse },
      //     undefined,
      //     "promptVisuals/parsePromptVisualsResponse",
      //   );
      // });

      await Promise.allSettled(visualsPromises);

      // get().saveStory(storyId);

      //   generationToast("Generation completed", {
      //     variant: "positive",
      //     actionLabel: "Create Presentation",
      //     onAction: () => {
      //       setState((state) => ({
      //         ...state,
      //         isExpressDialogOpen: true,
      //       }));
      //     },
      //   });

      // await new Promise(() => {
      //   setTimeout(() => {
      //     // generationToast("", { isGenerating: false })

      //     resolve();
      //   }, 10000);
      // });
    } catch (error) {
      console.error("Error parsing visuals response:", error);
      get().addToast(`Failed to generate story images`, {
        variant: "negative",
        timeout: -1,
        actionLabel: "Retry",
        shouldCloseOnAction: true,
        onAction: () => {
          get().fetchVisuals(storyId);
        },
      });
    }
  },
  isRegenerateVisualPending: false,
  /**
   * Regenerates the visual for a specific step in the story.
   *
   * @param {string} storyId - The ID of the story.
   * @param {string} stepId - The ID of the step.
   * @param {string} prompt - The prompt to use for generating the visual.
   * @param {string} seed - The seed to use for generating the visual.
   *
   * @returns {Promise<void>} - A promise that resolves when the visual is regenerated.
   * @throws {Error} If the step is not found or if the visual generation fails
   */
  regenerateVisual: async (storyId, stepId, prompt, seed) => {
    set({ isRegenerateVisualPending: true });
    const story = get().getStory(storyId);
    const step = story?.script[stepId];

    try {
      if (!step) {
        throw new Error(
          `No step found for index ${stepId} to regenerate visual`,
        );
      }

      const visualPrompt = prompt ?? step?.visualPrompt;
      const visualSeed =
        seed ?? story?.personas?.[step?.personas?.[0]]?.avatarSeed;

      if (!visualPrompt) {
        throw new Error(
          `No prompt found for step index ${stepId} to regenerate visual`,
        );
      }

      await get().fetchImageReference(storyId);

      const visualImages = await fetchFireflyImage(
        storyId,
        getFireflyImageBody({
          prompt: visualPrompt,
          ...(visualSeed ? { seeds: [visualSeed] } : {}),
          uploadId: get().imageReferenceResponse?.id,
          // imageReferenceUrl: get().imageReference?.internalUrl,
        }),
      );

      if (!visualImages?.length) {
        throw new Error(
          `Failed to regenerate visual for step ${stepId} with prompt: ${visualPrompt}`,
        );
      }

      // set(
      //   produce((state) => {
      //     const storyIndex = state.stories.findIndex(
      //       (story) => story.id === storyId,
      //     );
      //     if (storyIndex === -1) {
      //       throw new Error("Story not found");
      //     }

      //     // TODO: remove visualUrl and visualSeed from step
      //     // and use visuals array instead
      //     state.stories[storyIndex].script[stepId].visualUrl =
      //       visualImages[0].url;
      //     state.stories[storyIndex].script[stepId].visualSeed =
      //       visualImages[0].seed;
      //     state.stories[storyIndex].script[stepId].visuals =
      //       state.stories[storyIndex].script[stepId].visuals || [];
      //     state.stories[storyIndex].script[stepId].visuals.push({
      //       url: visualImages[0]?.url,
      //       path: visualImages[0]?.path,
      //       seed: visualImages[0]?.seed,
      //       prompt: visualPrompt,
      //     });
      //     state.stories[storyIndex].script[stepId].visual =
      //       state.stories[storyIndex].script[stepId].visuals.length - 1;
      //     state.isRegenerateVisualPending = false;
      //   }),
      //   undefined,
      //   "promptVisuals/regenerateVisual",
      // );

      // get().saveStory(storyId);
      queryClient.setQueryData(storyKey.detail(storyId), (oldData) =>
        produce(oldData, (state) => {
          state.script[stepId].visuals = state.script[stepId].visuals || [];
          state.script[stepId].visuals.push({
            url: visualImages[0]?.url,
            path: visualImages[0]?.path,
            seed: visualImages[0]?.seed,
            prompt: visualPrompt,
            type: "firefly",
          });
          state.script[stepId].visual = state.script[stepId].visuals.length - 1;
        }),
      );

      get().addToast(`Visual regenerated successfully`, {
        variant: "positive",
        timeout: 5000,
      });
    } catch (error) {
      console.error(`Failed to regenerate visual for step ${stepId}:`, error);
      set({ isRegenerateVisualPending: false });

      get().addToast(`Failed to regenerate visual`, {
        variant: "negative",
        timeout: -1,
        actionLabel: "Retry",
        shouldCloseOnAction: true,
        onAction: () => {
          get().regenerateVisual(storyId, stepId, prompt, seed);
        },
      });
    }
  },
  /**
   * Generates a similar visual for the given story and step.
   *
   * @param {string} storyId - The ID of the story.
   * @param {string} stepId - The ID of the step.
   * @param {string} prompt - The prompt to use for generating the visual.
   *
   * @returns {Promise<void>} - A promise that resolves when the similar visual is generated.
   * @throws {Error} If the step is not found or if the visual generation fails
   */
  generateSimilarVisual: async (storyId, stepId, prompt) => {
    get().regenerateVisual(
      storyId,
      stepId,
      prompt,
      get().getStory(storyId)?.script[stepId]?.visualSeed,
    );
  },
  /**
   * Regenerates the visual context for a specific step in the story.
   *
   * @param {string} storyId - The ID of the story.
   * @param {string} stepId - The ID of the step.
   * @param {*} update - The update to apply to the visual context.
   *
   * @returns {Promise<void>} - A promise that resolves when the visual context is regenerated.
   * @throws {Error} If the story or step is not found, or if the
   */
  regenerateVisualContext: async (storyId, stepId, update) => {
    set({ isRegenerateVisualPending: true });

    console.log("regenerateVisualContext", storyId, stepId, update);

    try {
      const story = get().getStory(storyId);
      if (!story) {
        throw new Error(`No story found with id: ${storyId}`);
      }

      if (!story.script.find((s) => s.id === stepId)) {
        throw new Error(`No step found with id: ${stepId}`);
      }

      get().updateStoryScriptItem(storyId, stepId, update);
      await get().generateVisuals(storyId, stepId);
      set({ isRegenerateVisualPending: false });
    } catch (error) {
      console.error("Error regenerating visual context:", error);
      set({ isRegenerateVisualPending: false });
    }
  },
  browseVisuals: (story) => {
    console.log("browseVisuals", story);
  },
  /**
   * Sets the image reference for the prompt visuals.
   *
   * @param {string} storyId - The ID of the story.
   * @param {string} imageReference - The image reference URL to set.
   *
   * @returns {void}
   */
  setImageReference: (storyId, imageReference) =>
    queryClient.setQueryData(storyKey.detail(storyId), (oldData) =>
      produce(oldData, (draft) => {
        if (draft) {
          draft.imageReference = imageReference;
        }
      }),
    ),
  /**
   * Generates visuals for the given story and step.
   *
   * @param {string} storyId - The ID of the story.
   * @param {string} stepId - The ID of the step.
   *
   * @returns {Promise<void>} - A promise that resolves when the visuals are generated.
   * @throws {Error} If the required data is missing or if the generation fails.
   */
  imageReferenceResponse: null,
  /**
   * Fetches the image reference and uploads it to Firefly storage.
   *
   * @param {string} storyId - The ID of the story.
   *
   * @returns {Promise<void>} - A promise that resolves when the image reference is fetched and uploaded.
   */
  fetchImageReference: async (storyId) => {
    const imageReference = get().getStory(storyId)?.imageReference;

    if (!imageReference) {
      console.warn("No image reference set, skipping fetch.");
      return;
    }

    if (get().imageReferenceResponse?.filePath === imageReference.filePath) {
      console.log("Image reference already fetched, skipping fetch.");
      return;
    }

    if (
      !get().imageReferenceResponse ||
      get().imageReferenceResponse?.filePath !== imageReference.filePath
    ) {
      try {
        const imageReferenceResponse = await uploadFireflyImage(
          imageReference.url,
        );

        set(
          { imageReferenceResponse },
          undefined,
          "promptVisuals/fetchImageReference",
        );
      } catch (error) {
        console.error("Failed to upload image reference:", error);
      }
    }
  },
});
